import { useGetUserInfoQuery, useIsFeatureEnabledQuery, UserRoles } from "@blox/api";
import { api, bffApi, coinDataApi, iPApi, userApi } from "@blox/api/api";
import { AxiosError } from "axios";
import jwt_decode from "jwt-decode";
import { User, UserManager } from "oidc-client-ts";

import { queryClient } from "~queryClient";

import configParams from "./env";

api.defaults.baseURL = configParams.API_BASE;
userApi.defaults.baseURL = configParams.AUTH_BASE;
coinDataApi.defaults.baseURL = configParams.COINDATA_URL;
iPApi.defaults.baseURL = configParams.IP_URL;
bffApi.defaults.baseURL = configParams.BFF_URL;

export const USER_SESSION_KEY = `oidc.user:${configParams.AUTH_BASE}/realms/blox:${configParams.CLIENT_ID}`;

const responseInterceptors: Parameters<typeof api.interceptors.response.use> = [
  (response) => {
    // Any status code that lie within the range of 2xx cause this function to trigger
    // Do something with response data
    return response;
  },
  async (error: AxiosError) => {
    // Any status codes that falls outside the range of 2xx cause this function to trigger
    // Do something with response error
    if (error.response?.status === 401) {
      console.error("401");
    }
    if (error.response?.status === 503) {
      // If we get a 503 set maintenance mode as enabled
      queryClient.setQueryData(useIsFeatureEnabledQuery.queryKey("BLOX"), {
        enabled: false,
      });
    }
    return Promise.reject(error);
  },
];

const checkUserRolesForForceRefresh = (currentTokenRoles: UserRoles[]) => {
  try {
    const userInfo = useGetUserInfoQuery.getQueryData();
    if (!userInfo || !currentTokenRoles?.length) {
      return false;
    }

    const hasDifferentRoles = currentTokenRoles?.some((role) => !userInfo.authorities?.includes(role));
    return hasDifferentRoles;
  } catch (error) {
    return false;
  }
};

let isRefreshing = false;
let refreshPromise: Promise<void> | null = null;
const requestQueue: (() => void)[] = [];

const requestInterceptors: Parameters<typeof api.interceptors.request.use> = [
  async (requestConfig) => {
    const sessionString = window.localStorage.getItem(USER_SESSION_KEY);

    if (sessionString) {
      const user = User.fromStorageString(sessionString);

      if (user.access_token) {
        const { resource_access } = jwt_decode<{
          resource_access: { ["blox-public"]: { roles: UserRoles[] } };
        }>(user.access_token);

        const forceRefresh = checkUserRolesForForceRefresh(resource_access?.["blox-public"]?.roles);

        if (forceRefresh) {
          if (!isRefreshing) {
            isRefreshing = true;
            refreshPromise = (async () => {
              try {
                const userManager = new UserManager({
                  authority: `${configParams.AUTH_BASE}/realms/blox`,
                  client_id: configParams.CLIENT_ID,
                  redirect_uri: window.location.origin,
                });
                const data = await userManager.signinSilent();
                window.localStorage.setItem(USER_SESSION_KEY, JSON.stringify(data));

                const updatedSession = window.localStorage.getItem(USER_SESSION_KEY);
                const updatedUser = User.fromStorageString(updatedSession!);
                user.access_token = updatedUser.access_token;
              } finally {
                isRefreshing = false;
                requestQueue.forEach((request) => request());
                requestQueue.length = 0;
              }
            })();
          }

          if (refreshPromise) {
            await new Promise<void>((resolve) => {
              requestQueue.push(resolve);
            });
          }
        }

        requestConfig!.headers!.Authorization = `Bearer ${user.access_token}`;
      }
    }
    return requestConfig;
  },
  (error) => {
    return Promise.reject(error);
  },
];

api.interceptors.response.use(...responseInterceptors);
api.interceptors.request.use(...requestInterceptors);

userApi.interceptors.response.use(...responseInterceptors);
userApi.interceptors.request.use(...requestInterceptors);
